const FILE_EXTENSIONS = /\.(mp4|webm|ogg|mov)$/i;

const parseUrl = (url) => {
   try {
      return new URL(url);
   } catch (error) {
      return null;
   }
};

const youTubeId = (parsed) => {
   const host = parsed.hostname.replace(/^(www\.|m\.)/, "");
   if (host === "youtu.be") return parsed.pathname.split("/")[1] || null;
   if (host !== "youtube.com" && host !== "youtube-nocookie.com") return null;
   if (parsed.pathname === "/watch") return parsed.searchParams.get("v");
   const [, kind, id] = parsed.pathname.split("/");
   return ["embed", "shorts", "live"].includes(kind) ? id || null : null;
};

/**
 * Turns the link a video was published under into something the player can
 * show: an embeddable iframe url for YouTube, Vimeo, Loom and Storylane
 * demos, or the file itself for a direct .mp4/.webm link. Returns null for anything else so the
 * library can leave the entry out instead of showing a broken player.
 */
export const getVideoSource = (url) => {
   const parsed = typeof url === "string" ? parseUrl(url.trim()) : null;
   if (!parsed) return null;

   const youTube = youTubeId(parsed);
   if (youTube) {
      return {
         type: "iframe",
         src: `https://www.youtube-nocookie.com/embed/${youTube}?rel=0`,
         thumbnail: `https://i.ytimg.com/vi/${youTube}/mqdefault.jpg`
      };
   }

   const host = parsed.hostname.replace(/^www\./, "");
   if (host === "vimeo.com" || host === "player.vimeo.com") {
      const id = parsed.pathname.split("/").find((part) => /^\d+$/.test(part));
      if (id) {
         return { type: "iframe", src: `https://player.vimeo.com/video/${id}`, thumbnail: null };
      }
   }

   if (host === "loom.com") {
      const [, kind, id] = parsed.pathname.split("/");
      if (["share", "embed"].includes(kind) && id) {
         return { type: "iframe", src: `https://www.loom.com/embed/${id}`, thumbnail: null };
      }
   }

   // Storylane interactive demos: the share link (/share/<id>) or the demo
   // itself (/demo/<id>), on app.storylane.io or a custom Storylane subdomain.
   if (host === "storylane.io" || host.endsWith(".storylane.io")) {
      const [, kind, id] = parsed.pathname.split("/");
      if (["share", "demo"].includes(kind) && id) {
         return {
            type: "iframe",
            src: `${parsed.origin}/demo/${id}?embed=inline`,
            thumbnail: null,
            interactive: true
         };
      }
   }

   if (FILE_EXTENSIONS.test(parsed.pathname)) {
      return { type: "file", src: parsed.href, thumbnail: null };
   }

   return null;
};

/** Attaches each video's player source and drops the ones it can't play. */
export const getPlayableVideos = (videos = []) =>
   videos
      .map((video) => ({ ...video, source: getVideoSource(video?.url) }))
      .filter((video) => video.source && video.title);

/** Fields the video search looks in. */
export const videoSearchFields = (video) => [video.title, video.description, video.category];
